import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { CopilotMascot } from '@/shared/ui/CopilotMascot';
import type { MascotState } from '@/shared/ui/CopilotMascot';

import { useCopilotThread } from '../api/thread';
import { onCopilotOpenRequest } from '../model/openRequest';

import { TurnView } from './CopilotTurn';

/* The Copilot, one click from any page: a top-bar button and a drawer read against the page.
 *
 * **A drawer rather than a dialog, which is the design's call and the right one.** A dialog asks to
 * be answered before anything else; a question about the table on screen is asked *about* that
 * screen, so the page stays visible behind a light scrim and the drawer slides over its edge. The
 * full console at `/copilot` stays the place for a long investigation — this is the quick question.
 *
 * **The answer is presented by the same rules as the console**, through the same model helpers:
 * `provenanceOf` says who wrote the words (only `answered` carries model prose), `refusalSummary`
 * says which kinds the asker could not read, and a status is a word as well as a colour. A second
 * implementation of those rules would be how the drawer and the console come to disagree about
 * whether a sentence was a model's.
 *
 * **A citation navigates and closes the drawer** for the kinds whose reference is a record id this
 * product has a page for. Any other kind stays a plain chip: a link that resolved to a 404 would be
 * worse than one that does not pretend to go anywhere.
 */

/** Starter questions per rail group, keyed to copy entries. A group with none borrows the default. */
const SUGGESTIONS: Readonly<Record<string, readonly string[]>> = {
  home: [
    'copilotDrawer.suggest.whyEstate',
    'copilotDrawer.suggest.attention',
    'copilotDrawer.suggest.incidents',
  ],
  data: [
    'copilotDrawer.suggest.neverProfiled',
    'copilotDrawer.suggest.pii',
    'copilotDrawer.suggest.downstream',
  ],
  quality: [
    'copilotDrawer.suggest.whyEstate',
    'copilotDrawer.suggest.costliestRules',
    'copilotDrawer.suggest.incidents',
  ],
  default: ['copilotDrawer.suggest.attention', 'copilotDrawer.suggest.whyEstate'],
};

export function CopilotLauncher({
  group,
  context,
}: {
  /** The rail group the reader is in, which picks the starter questions. */
  readonly group: string;
  /** What the page is about, in words — shown as "Asking about …". */
  readonly context: string;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /* The conversation is shared with the `/copilot` page — see `model/thread`. */
  const { turns: thread, asking, send: ask, clear } = useCopilotThread();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const launcher = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const close = useCallback(() => {
    setOpen(false);
    launcher.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    input.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  /* A page's "Ask Copilot" opens this drawer. A question it carries lands in the composer unsent —
     see `openRequest` for why pressing a button must not spend a completion. */
  useEffect(
    () =>
      onCopilotOpenRequest((request) => {
        if (request.question !== undefined) setDraft(request.question);
        setOpen(true);
      }),
    [],
  );

  /* Follow the newest turn. `scrollTop` rather than `scrollIntoView`, which moves every scrolling
     ancestor — the page behind the drawer included — and which jsdom does not implement. */
  useEffect(() => {
    const scroller = body.current;
    if (scroller !== null) scroller.scrollTop = scroller.scrollHeight;
  }, [thread]);

  const send = (question: string): void => {
    if (question.trim() === '' || asking) return;
    setDraft('');
    ask(question);
  };

  const onNavigate = (to: string): void => {
    setOpen(false);
    navigate(to);
  };

  const last = thread[thread.length - 1];
  const mascot: MascotState = asking
    ? 'thinking'
    : last?.state === 'failed'
      ? 'error'
      : last?.state === 'answered'
        ? 'success'
        : 'idle';
  const suggestions = SUGGESTIONS[group] ?? SUGGESTIONS['default'] ?? [];

  return (
    <>
      <button
        ref={launcher}
        type="button"
        className="lq-cop__launch"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(!open);
        }}
      >
        <CopilotMascot state={asking ? 'thinking' : 'idle'} size="sm" />
        <span>{t('copilotDrawer.launch')}</span>
      </button>

      <div
        className="lq-cop__scrim"
        data-open={open}
        aria-hidden="true"
        onClick={close}
      />
      <aside
        className="lq-cop__drawer"
        data-open={open}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-hidden={!open}
        {...(open ? {} : { inert: '' })}
      >
        <header className="lq-cop__head">
          <CopilotMascot state={mascot} size="md" />
          <div className="lq-cop__headText">
            <h2 id={titleId} className="lq-cop__title">
              {t('copilotDrawer.title')}
            </h2>
            <p className="lq-cop__about">{t('copilotDrawer.about', { context })}</p>
          </div>
          {thread.length > 0 ? (
            <button type="button" className="lq-cop__clear" onClick={clear}>
              {t('copilotDrawer.clear')}
            </button>
          ) : null}
          <button
            type="button"
            className="lq-icon-button"
            aria-label={t('copilotDrawer.close')}
            onClick={close}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="lq-cop__body" ref={body}>
          {thread.length === 0 ? (
            <div className="lq-cop__empty">
              <CopilotMascot state="idle" size="xl" />
              <h3 className="lq-cop__emptyTitle">{t('copilotDrawer.emptyTitle')}</h3>
              <p className="lq-cop__emptyText">{t('copilotDrawer.emptyText')}</p>
              <div className="lq-cop__suggest">
                {suggestions.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className="lq-cop__suggestion"
                    onClick={() => {
                      send(t(key));
                    }}
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <ol className="lq-cop__thread">
              {thread.map((turn) => (
                <TurnView key={turn.id} turn={turn} onNavigate={onNavigate} />
              ))}
            </ol>
          )}
        </div>

        <footer className="lq-cop__composer">
          <form
            className="lq-cop__form"
            onSubmit={(event) => {
              event.preventDefault();
              send(draft);
            }}
          >
            <input
              ref={input}
              className="lq-cop__input"
              type="text"
              value={draft}
              maxLength={2000}
              placeholder={t('copilotDrawer.placeholder')}
              aria-label={t('copilotDrawer.placeholder')}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
            <button
              type="submit"
              className="lq-cop__send"
              disabled={draft.trim() === '' || asking}
            >
              {t('copilotDrawer.send')}
            </button>
          </form>
          <p className="lq-cop__foot">
            {t('copilotDrawer.readOnly')}{' '}
            <Link
              to={SCREEN_PATHS.copilot}
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('copilotDrawer.openConsole')}
            </Link>
          </p>
        </footer>
      </aside>
    </>
  );
}
